import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Only import the default language (English) initially
import enTranslation from '../translations/en.json';

// Get saved language preference with improved mobile detection
const getInitialLanguage = (): string => {
  // First, check localStorage for saved preference
  const savedLang = localStorage.getItem('language');
  if (savedLang && ['en', 'es', 'fr', 'hi', 'de', 'it', 'ja', 'nl', 'pt', 'ru', 'ar'].includes(savedLang)) {
    return savedLang;
  }
  
  // Then, try to detect browser language with better mobile support
  const browserLang = navigator.language?.split('-')[0];
  if (browserLang && ['en', 'es', 'fr', 'hi', 'de', 'it', 'ja', 'nl', 'pt', 'ru', 'ar'].includes(browserLang)) {
    return browserLang;
  }
  
  // Try navigator.languages array (more reliable on mobile)
  if (navigator.languages && navigator.languages.length > 0) {
    for (const lang of navigator.languages) {
      const langCode = lang.split('-')[0];
      if (['en', 'es', 'fr', 'hi', 'de', 'it', 'ja', 'nl', 'pt', 'ru', 'ar'].includes(langCode)) {
        return langCode;
      }
    }
  }
  
  return 'en'; // Default fallback
};

const initialLanguage = getInitialLanguage();

// Initial resources - load only English initially
const resources: Record<string, any> = {
  en: {
    translation: enTranslation,
  },
};

// If initial language is not English, we'll load it dynamically
const shouldLoadInitialLanguage = initialLanguage !== 'en';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLanguage, // Set the initial language
    fallbackLng: 'en',
    debug: false, // Disable debug logging
    returnObjects: true,
    
    // Interpolation options
    interpolation: {
      escapeValue: false, // React already safes from xss
    },

    // React i18next options
    react: {
      useSuspense: true,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },

    // Lazy loading configuration
    load: 'languageOnly', // Only load language, not namespaces
    preload: ['en'], // Only preload English
    
    // Language detector options
    detection: {
      // Order and from where user language should be detected
      order: ['localStorage', 'navigator', 'htmlTag'],
      
      // Keys or params to lookup language from
      lookupLocalStorage: 'language',
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,
      
      // Cache user language on
      caches: ['localStorage'],
    },
  });

// Cache for loaded languages to avoid repeated imports
const loadedLanguages = new Set(['en']);

// Function to dynamically load a language using dynamic imports
export const loadLanguage = async (language: string): Promise<void> => {
  if (i18n.hasResourceBundle(language, 'translation') || loadedLanguages.has(language)) {
    return; // Already loaded
  }

  try {
    // Use dynamic import to load translation files
    // This works in both development and production builds
    const module = await import(`../translations/${language}.json`);
    const translations = module.default;
    
    i18n.addResourceBundle(language, 'translation', translations, true, true);
    loadedLanguages.add(language);
    
  } catch (error) {
    console.error(`❌ Failed to load ${language} translations:`, error);
    // Fallback to English
    if (language !== 'en') {
      console.log(`🔄 Falling back to English for ${language}`);
      await loadLanguage('en');
    }
  }
};

// Function to preload languages in background - OPTIMIZED for memory efficiency
export const preloadLanguages = async (languages: string[]): Promise<void> => {
  const MAX_LANGUAGES_IN_CACHE = 2; // Ultra-aggressive: only 2 languages (was 3)
  
  // Ultra-aggressive cleanup: if we have more than 2 languages, remove oldest
  if (loadedLanguages.size > MAX_LANGUAGES_IN_CACHE) {
    const languagesToRemove = Array.from(loadedLanguages).filter(lang => 
      lang !== 'en' && 
      lang !== i18n.language && // Don't remove current language
      !languages.includes(lang) // Don't remove languages being requested
    );
    
    // Remove oldest languages to keep only 2
    const languagesToRemoveCount = loadedLanguages.size - MAX_LANGUAGES_IN_CACHE;
    for (const langToRemove of languagesToRemove.slice(0, languagesToRemoveCount)) {
      if (i18n.hasResourceBundle(langToRemove, 'translation')) {
        i18n.removeResourceBundle(langToRemove, 'translation');
        loadedLanguages.delete(langToRemove);
        console.log(`🗑️ Removed ${langToRemove} from cache to save memory (ultra-aggressive mode)`);
      }
    }
  }
  
  // Only load languages that are explicitly requested and not already loaded
  const languagesToLoad = languages
    .filter(lang => lang !== 'en' && !i18n.hasResourceBundle(lang, 'translation'))
    .slice(0, 2); // Limit to 2 languages at a time for 24+ language support
  
  if (languagesToLoad.length > 0) {
    console.log(`🔄 Loading ${languagesToLoad.length} languages on demand: ${languagesToLoad.join(', ')}`);
    const promises = languagesToLoad.map(lang => loadLanguage(lang));
    await Promise.allSettled(promises);
  } else {
    console.log(`✅ All requested languages already loaded`);
  }
};

// Load the initial language if it's not English
if (shouldLoadInitialLanguage) {
  loadLanguage(initialLanguage).then(() => {
    // Change to the loaded language
    i18n.changeLanguage(initialLanguage);
  }).catch(error => {
    console.error('Failed to load initial language:', error);
    // Fallback to English
    i18n.changeLanguage('en');
  });
}

// Test function to verify language switching
export const testLanguageSwitching = async () => {
  console.log('🧪 Testing language switching...');
  console.log('Current language:', i18n.language);
  console.log('Loaded languages:', Array.from(loadedLanguages));
  
  // Get initial memory info if available
  if ('memory' in performance) {
    console.log('Initial memory usage:', (performance as any).memory);
  }
  
  // Test switching to Spanish
  await loadLanguage('es');
  await i18n.changeLanguage('es');
  console.log('After switching to Spanish:', i18n.language);
  console.log('Spanish translation test:', i18n.t('header.title'));
  
  // Test switching to French
  await loadLanguage('fr');
  await i18n.changeLanguage('fr');
  console.log('After switching to French:', i18n.language);
  console.log('French translation test:', i18n.t('header.title'));
  
  // Test switching back to English
  await i18n.changeLanguage('en');
  console.log('After switching back to English:', i18n.language);
  console.log('English translation test:', i18n.t('header.title'));
  
  console.log('✅ Language switching test completed');
  console.log('Final loaded languages:', Array.from(loadedLanguages));
  
  // Get final memory info if available
  if ('memory' in performance) {
    console.log('Final memory usage:', (performance as any).memory);
  }
};

// Function to get memory usage information
export const getMemoryInfo = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    return {
      usedJSHeapSize: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
      totalJSHeapSize: Math.round(memory.totalJSHeapSize / 1024 / 1024) + 'MB',
      jsHeapSizeLimit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + 'MB',
      loadedLanguages: Array.from(loadedLanguages),
      resourceBundles: Object.keys(i18n.store.data || {})
    };
  }
  return {
    loadedLanguages: Array.from(loadedLanguages),
    resourceBundles: Object.keys(i18n.store.data || {}),
    memoryInfo: 'Not available in this browser'
  };
};

// Function to clear language cache (for testing memory optimization)
export const clearLanguageCache = () => {
  const languagesToRemove = Array.from(loadedLanguages).filter(lang => lang !== 'en');
  
  for (const langToRemove of languagesToRemove) {
    if (i18n.hasResourceBundle(langToRemove, 'translation')) {
      i18n.removeResourceBundle(langToRemove, 'translation');
      loadedLanguages.delete(langToRemove);
      console.log(`🗑️ Removed ${langToRemove} from cache`);
    }
  }
  
  console.log('✅ Language cache cleared');
  console.log('Remaining languages:', Array.from(loadedLanguages));
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testLanguageSwitching = testLanguageSwitching;
  (window as any).getMemoryInfo = getMemoryInfo;
  (window as any).clearLanguageCache = clearLanguageCache;
}

export default i18n; 